import type { ChangeEvent } from "react";
import { useEffect, useState } from "react";
import { minuteMs } from "verdi-common/timeConstants";
import { Input, type InputProps } from "@/components/ui/input";
import { cn } from "@/utils/class-name";
import { formatDurationLabel } from "@/utils/format-duration-label";

/**
 * Parses a raw digit string (1–4 digits) to milliseconds.
 * Digits are treated as the rightmost digits (left-padded with zeros).
 * e.g. "5" → 00:05, "123" → 01:23, "1234" → 12:34.
 * Minutes are clamped to [0, 59].
 */
const parseRawDigitsToMs = (raw: string): number => {
    const padded = raw.padStart(4, "0");
    const hours = parseInt(padded.slice(0, 2), 10);
    const minutes = parseInt(padded.slice(2), 10);
    return (hours * 60 + Math.min(minutes, 59)) * minuteMs;
};

interface TimeInputProps extends Pick<InputProps, "className" | "inputClassName"> {
    /** Duration in milliseconds */
    value: number;
    onChange: (ms: number) => void;
}

/**
 * A duration input that displays time as "HHh MMm" (or just "Xm" when hours = 0).
 *
 * - Value and onChange use milliseconds.
 * - The h/m labels are stripped on focus; users only type digits.
 * - External value changes (e.g. from +5m / -15m buttons) update the display
 *   whenever the input is not focused.
 * - On blur the display is normalized back to the formatted form.
 */
export function TimeInput({ value, onChange, className, inputClassName }: TimeInputProps) {
    const [inputValue, setInputValue] = useState(() => formatDurationLabel(value));
    const [isFocused, setIsFocused] = useState(false);

    useEffect(() => {
        if (!isFocused) {
            setInputValue(formatDurationLabel(value));
        }
    }, [value, isFocused]);

    const handleFocus = () => {
        setIsFocused(true);
        setInputValue((prev) => prev.replace(/[^0-9]/g, ""));
    };

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value.replace(/[^0-9]/g, "").slice(0, 4);
        setInputValue(raw);
        onChange(parseRawDigitsToMs(raw));
    };

    const handleBlur = () => {
        setIsFocused(false);
        setInputValue(formatDurationLabel(value));
    };

    return (
        <Input
            type={"text"}
            inputMode={"numeric"}
            value={inputValue}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            className={cn("font-mono", className)}
            inputClassName={inputClassName}
        />
    );
}
